/**
 * Sign-in redirect for protected AppStore portal paths.
 *
 * Protected paths (/dashboard, /analytics) are meant to be gated by Traefik forward-auth, which redirects an
 * unauthenticated visitor to Authentik before the page loads. If that gate is missing or misrouted, the SPA is
 * served anyway and `/.auth/me` reports unauthenticated. The SPA then sends the visitor to the Authentik outpost
 * itself, once, instead of showing text that promises a redirect nothing performs.
 */

export const OUTPOST_START_PATH = '/outpost.goauthentik.io/start'

const GUARD_KEY = 'appstore.loginRedirectAt'
const GUARD_WINDOW_MS = 60_000

export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/** Outpost URL that signs the user in, then returns them to `currentHref`. */
export function buildLoginUrl(currentHref: string): string {
  return `${OUTPOST_START_PATH}?rd=${encodeURIComponent(currentHref)}`
}

/**
 * True when the SPA may redirect on its own. The outpost path is not routed when the Traefik gate is absent, so it
 * serves this same SPA; without a guard that would bounce forever. One automatic attempt per window, never from the
 * outpost path, and never when storage is unusable (the sign-in button still works in all of those cases).
 */
export function shouldAutoRedirect(storage: StorageLike | null, now: number, pathname: string): boolean {
  if (!storage) return false
  if (pathname.startsWith('/outpost.goauthentik.io')) return false
  try {
    const last = Number(storage.getItem(GUARD_KEY))
    if (Number.isFinite(last) && last > 0 && now - last < GUARD_WINDOW_MS) return false
    storage.setItem(GUARD_KEY, String(now))
    return true
  } catch {
    return false
  }
}
